<template>
  <div class="surgery-report-container container-fluid py-4">
    <!-- Header Section -->
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
      <h3 class="report-title">
        <i class="el-icon-s-data mr-2"></i>
        Surgery Report
      </h3>
      <el-button type="primary" size="small" icon="el-icon-download" :disabled="!surgeryRecords.length"
        @click="downloadReport">
        Download CSV
      </el-button>
    </div>

    <!-- Filter Section -->
    <div class="row align-items-end mb-4">
      <div class="col-md-2 col-sm-12 mb-2">
        <label class="fw-bold text-secondary">Select Month</label>
        <el-date-picker v-model="month" type="month" placeholder="Pick a month" class="w-100"></el-date-picker>
      </div>
      <div class="col-auto mb-2">
        <el-button type="primary" @click="getSurgeonReport">Get Report</el-button>
      </div>
    </div>


    <!-- Summary Section -->
    <div class="summary-section mb-4 d-flex flex-wrap justify-content-between">
      <div v-for="(item, index) in summaryCards" :key="index" class="flex-fill">
        <el-card shadow="hover" class="summary-card" :class="item.color">
          <div class="summary-top">
            <i :class="item.icon"></i>
            <span class="summary-title">{{ item.title }}</span>
          </div>
          <div class="summary-value">{{ item.value }}</div>
        </el-card>
      </div>
    </div>
    <el-card shadow="hover" v-if="surgeryRecords.length" class="records-card">
      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <colgroup>
            <col style="width: 50px" />
            <col style="width: 140px" />
            <col style="width: 180px" />
            <col style="width: 80px" />
            <col style="width: 120px" />
            <col style="width: 160px" />
            <col style="width: 140px" />
            <col style="width: 120px" />
            <col style="width: 160px" />
            <col style="width: 300px" />
          </colgroup>
          <thead class="table-light">
            <tr>
              <th>#</th>
              <th>Operation Date</th>
              <th>Surgeon</th>
              <th>Gender</th>
              <th>Duration (min)</th>
              <th>Result</th>
              <th>Pregnancy Stage</th>
              <th>Post Op Hold</th>
              <th>Rabies Vaccine</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in surgeryRecords" :key="record.id">
              <td>{{ record.id }}</td>
              <td>{{ (record.operation_date) }}</td>
              <td>
                {{ record.surgeon_name }}
              </td>
              <td>
                {{ record.gender || '-' }}
              </td>
              <td>{{ record.duration }}</td>
              <td>
                {{ record.operation_result }}
              </td>
              <td>{{ record.pregnancy_stage || '-' }}</td>
              <td>{{ record.post_op_hold ? "Yes" : "No" }}</td>
              <td>{{ record.rabies_vaccine || '-' }}</td>
              <td>{{ record.note || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </el-card>

    <!-- Empty State -->
    <div v-else class="text-center py-5 text-muted">
      <i class="el-icon-document-remove" style="font-size: 48px"></i>
      <p class="mt-3">No surgery records found for this month.</p>
    </div>

  </div>
</template>

<script>
import { Card, Button, Tag, Message, DatePicker } from 'element-ui';
import apiService from '../../../../services/api.service';

export default {
  components: {
    [Button.name]: Button,
    [DatePicker.name]: DatePicker,
    [Tag.name]: Tag,
    [Card.name]: Card,
  },
  props: {
    projectId: {
      type: String | Number,
      required: true
    }
  },
  data() {
    return {
      surgeryStats: [],
      surgeryRecords: [],
      month: new Date()
    };
  },
  computed: {
    summaryCards() {
      const d = this.surgeryStats;
      if (!d) return [];
      return [
        {
          title: "Total Surgeries",
          value: d.total_surgeries,
          color: "blue",
          icon: "el-icon-s-data",
        },
        {
          title: "Total Time (mins)",
          value: d.total_duration_mins,
          color: "orange",
          icon: "el-icon-time",
        },
        {
          title: "Avg Time (mins)",
          value: d.average_time_mins,
          color: "green",
          icon: "el-icon-time",
        },
        {
          title: "Spay (Female)",
          value: d.spay_female,
          color: "pink",
          icon: "el-icon-female",
        },
        {
          title: "Castration (Male)",
          value: d.castration_male,
          color: "purple",
          icon: "el-icon-male",
        },
      ];
    },
  },
  methods: {
    async getSurgeonReport() {
      const vm = this;
      const loading = vm.$loading({
        lock: true,
        text: 'Loading statistics...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      try {
        const date = new Date(this.month);
        const formattedMonth = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0');
        const apiResponse = await apiService.getSurgeryMonthlyReport(vm.projectId, formattedMonth);
        const { response } = apiResponse;
        if (response.error && response.error.error_code === 0) {
          vm.surgeryRecords = response.data.records;
          vm.surgeryStats = response.data.summary;
        } else {
          Message.error('Failed to load surgery statistics');
        }
      } catch (error) {
        console.error('Error fetching surgeon statistics:', error);
        Message.error('An error occurred while fetching surgeon statistics');
      } finally {
        loading.close();
      }
    },
    async downloadReport() {
      const vm = this;
      const loading = vm.$loading({
        lock: true,
        text: 'Downloading...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      });
      try {
        const project = vm.$store.getters.getCurrentSelectedProject;
        console.log("🚀 ~ project:", project)
        const date = new Date(this.month);
        const formattedMonth = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0');
        const payload = {
          month: formattedMonth,
          format: 'csv'
        }
        const apiResponse = await apiService.downloadSurgeyMonthlyReport(vm.projectId, payload);
        const url = window.URL.createObjectURL(new Blob([apiResponse]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${formattedMonth}_Project_${project.name}_Surgery_Report.csv`); // Filename
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('Error downloading surgery report:', error);
        Message.error('Failed to download surgery report');
      } finally {
        loading.close();
      }

    },
    exportToCSV() { }
  },
  mounted() {
    this.getSurgeonReport();
  }
};
</script>

<style scoped>
.surgery-report-container {
  min-height: 100vh;
}

.report-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.summary-section {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;
}

.summary-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #fff;
  border-radius: 12px;
  min-height: 100px;
}

.summary-top {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  opacity: 0.85;
}

.summary-top i {
  font-size: 20px;
}

.summary-value {
  font-size: 24px;
  font-weight: bold;
  margin-top: 6px;
}

.summary-card.blue {
  background: linear-gradient(135deg, #2196f3, #64b5f6);
}

.summary-card.green {
  background: linear-gradient(135deg, #43a047, #81c784);
}

.summary-card.pink {
  background: linear-gradient(135deg, #ec407a, #f48fb1);
}

.summary-card.purple {
  background: linear-gradient(135deg, #8e24aa, #ba68c8);
}

.summary-card.orange {
  background: linear-gradient(135deg, #009688, #4db6ac);
}

.records-card {
  border-radius: 10px;
  background-color: #fff;
}

.table {
  border-radius: 10px;
  overflow: hidden;
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
}

.table th {
  color: #555;
  font-weight: 600;
  text-wrap: wrap;
  overflow: hidden;
}

.table td {
  color: #444;
  text-wrap: wrap;
  white-space: normal;
}

.table td,
.table th {
  text-align: left;
  padding: 0.75rem;
  vertical-align: middle;
}

.table-responsive {
  overflow-x: auto;
}

.table-hover tbody tr:hover {
  background-color: #f5f7fa;
}
</style>
